/**
 * EntityOverrideSystem — Applies temporary visual overrides (tint, alpha, scale)
 * to entity sprites and reverts them after a duration.
 * Extracted from VFXEffectPlayer.
 */

import type { VFXPrimitive, VFXEffectConfig } from '../VFXEffectRegistry';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { createGraphics } from '../../rendering/utils/Graphics';

interface OverrideData {
  sprite: any;
  tint: number;
  alpha: number;
  scale: number;
}

export class EntityOverrideSystem {
  private overrides = new Map<string, OverrideData>();

  /** Apply an entity override from a VFXPrimitive config. */
  public apply(
    scene: unknown,
    prim: VFXPrimitive,
    overrides?: { scale?: number; duration?: number; targetId?: string },
    config?: VFXEffectConfig,
  ): void {
    const followTargetId = overrides?.targetId || config?.followTarget;
    const registry = ServiceRegistry.getInstance();
    const entitySystem = registry.has('EntitySystem') ? registry.get('EntitySystem') as any : null;
    const target = followTargetId
      ? entitySystem?.getEntity?.(followTargetId) || entitySystem?.getPlayerManager?.()?.getPlayer?.()
      : null;
    const sprite = target?.sprite || target;

    if (!sprite?.setTint) {
      // Visual fallback: spawn a colored flash at the effect position when
      // no sprite is found. Substrate-agnostic: IGraphics circle + rAF
      // fade+grow loop. (Migrated from scene.add.circle + scene.tweens.add
      // in Stage 5q.)
      if (prim.entityTint !== undefined && (scene as any)?.add) {
        const flashColor = prim.entityTint;
        const flashDuration = prim.tween?.duration || 200;
        const gfx = createGraphics(scene, { depth: 94 });
        gfx.fillCircle(0, 0, 25, flashColor, 0.4);

        const startTime = performance.now();
        const tick = () => {
          const elapsed = performance.now() - startTime;
          const t = Math.min(elapsed / flashDuration, 1);
          gfx.setAlpha(1 - t);
          gfx.setScale(1 + t);
          if (t < 1) requestAnimationFrame(tick);
          else gfx.destroy();
        };
        requestAnimationFrame(tick);
      }
      return;
    }

    const overrideKey = `${followTargetId || 'global'}_${config?.id || 'override'}`;
    if (!this.overrides.has(overrideKey)) {
      this.overrides.set(overrideKey, {
        sprite,
        tint: sprite.tintTopLeft,
        alpha: sprite.alpha,
        scale: sprite.scaleX,
      });
    }

    if (prim.entityTint !== undefined) sprite.setTint(prim.entityTint);
    if (prim.entityAlpha !== undefined) sprite.setAlpha(prim.entityAlpha);
    if (prim.entityScale !== undefined) sprite.setScale(prim.entityScale * (overrides?.scale || 1));

    const duration = overrides?.duration || config?.duration || prim.tween?.duration || 200;
    // Substrate-agnostic timer (was scene.time.delayedCall).
    setTimeout(() => this.revert(overrideKey), duration);
  }

  /** Revert a specific override by key. */
  public revert(key: string): void {
    const original = this.overrides.get(key);
    if (original?.sprite?.active && original.sprite.scene) {
      original.sprite.clearTint();
      original.sprite.setAlpha(original.alpha);
      original.sprite.setScale(original.scale);
    }
    this.overrides.delete(key);
  }

  /** Revert all overrides matching an instance ID substring. */
  public revertMatching(substring: string): void {
    for (const key of Array.from(this.overrides.keys())) {
      if (key.includes(substring)) this.revert(key);
    }
  }

  public clear(): void {
    this.overrides.clear();
  }
}
